import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MapPin, Phone, Mail, Home as HomeIcon, Euro, TrendingUp,
  ChevronDown, Star, Anchor, Sun, Trees,
  Calculator, Send, CheckCircle, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import {
  RENTAL_DATA, EXCURSIONS, SEASON_COLORS, SEASON_LABELS,
  calculateFinancing, formatCurrency, scrollToSection
} from '@/lib/index';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LineChart, Line, Legend
} from 'recharts';

// ─── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [open, setOpen] = useState(false);
  const navLinks = [
    { label: 'Die Wohnung',  id: 'property'   },
    { label: 'Vermietung',   id: 'rental'      },
    { label: 'Finanzierung', id: 'financing'   },
    { label: 'Ausflüge',     id: 'excursions'  },
    { label: 'Rügen',        id: 'ruegen'      },
    { label: 'Kontakt',      id: 'contact'     },
  ];
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 shadow-sm" style={{ backdropFilter: 'blur(12px)' }}>
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
        <button
          onClick={() => scrollToSection('hero')}
          className="flex items-center gap-2 font-semibold text-primary text-lg"
        >
          <Anchor className="w-5 h-5 text-accent" />
          <span style={{ fontFamily: "'Playfair Display', serif" }}>A57 · Rügen</span>
        </button>
        {/* Desktop */}
        <ul className="hidden md:flex items-center gap-6 text-sm font-medium text-foreground/70">
          {navLinks.map(l => (
            <li key={l.id}>
              <button
                onClick={() => scrollToSection(l.id)}
                className="hover:text-primary transition-colors duration-200 cursor-pointer"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>
        <button
          onClick={() => scrollToSection('contact')}
          className="hidden md:inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <Euro className="w-4 h-4" /> Anfragen
        </button>
        {/* Mobile */}
        <button className="md:hidden text-foreground" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-border px-4 py-4 flex flex-col gap-3">
          {navLinks.map(l => (
            <button
              key={l.id}
              className="text-left text-sm font-medium text-foreground/80 hover:text-primary"
              onClick={() => { scrollToSection(l.id); setOpen(false); }}
            >
              {l.label}
            </button>
          ))}
          <button
            className="mt-2 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold"
            onClick={() => { scrollToSection('contact'); setOpen(false); }}
          >
            Jetzt anfragen – 140.000 €
          </button>
        </div>
      )}
    </nav>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section id="hero" className="relative h-screen min-h-[600px] flex items-end overflow-hidden">
      <img
        src="/images/sunset_a57.jpg"
        alt="Ferienwohnung A57 Rügen Sonnenuntergang"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-flex items-center gap-2 bg-accent/90 text-accent-foreground px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <MapPin className="w-3.5 h-3.5" /> Bakenberg · Wittow · Insel Rügen
          </span>
          <h1
            className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Mehr Meer<br />geht nicht!
          </h1>
          <p className="text-white/85 text-lg md:text-xl max-w-2xl mb-8">
            Ihr eigener Platz an der Ostsee – Ferienwohnung A57 in traumhafter Strandlage.
            <span className="text-accent font-bold"> Kaufpreis: 140.000 €</span>
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => scrollToSection('property')}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold text-base hover:opacity-90 transition-all hover:scale-105 shadow-lg"
            >
              <HomeIcon className="w-5 h-5" /> Die Wohnung entdecken
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="flex items-center gap-2 bg-white/15 text-white border border-white/40 px-6 py-3 rounded-full font-semibold text-base hover:bg-white/25 transition-all"
            >
              <Send className="w-5 h-5" /> Jetzt anfragen
            </button>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 flex flex-col items-center gap-1"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 1.8 }}
      >
        <span className="text-xs uppercase tracking-widest">Entdecken</span>
        <ChevronDown className="w-5 h-5" />
      </motion.div>
    </section>
  );
}

// ─── Highlights bar ────────────────────────────────────────────────────────────
function HighlightsBar() {
  const items = [
    { icon: <MapPin className="w-5 h-5" />, label: 'Direkter Strandnähe', sub: '< 500 m zum Strand' },
    { icon: <Euro className="w-5 h-5" />, label: 'Kaufpreis', sub: '140.000 €' },
    { icon: <TrendingUp className="w-5 h-5" />, label: 'Mietrendite', sub: 'bis 18.000 € / Jahr' },
    { icon: <Sun className="w-5 h-5" />, label: 'Sonnenstunden', sub: 'Überdurchschnittlich' },
    { icon: <Star className="w-5 h-5" />, label: 'Größte deutsche Insel', sub: 'Insel Rügen' },
  ];
  return (
    <section className="bg-primary text-primary-foreground py-5">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="flex items-center gap-3"
            >
              <div className="text-accent shrink-0">{item.icon}</div>
              <div>
                <div className="font-semibold text-sm">{item.label}</div>
                <div className="text-primary-foreground/70 text-xs">{item.sub}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Property Section ──────────────────────────────────────────────────────────
function PropertySection() {
  const features = [
    '≈ 500 m zum Strand – Ostseeluft pur',
    'Ruhige Waldrandlage mit Meeresblick',
    'Voll möbliert & sofort bezugsfertig',
    'Eigene Stellplätze direkt am Haus',
    'Professionelle Ferienwohnungsanlage',
    'Ideale Kapitalanlage & Eigennutzung',
    'Haustiere auf Anfrage willkommen',
    'Ganzjährig vermietbar',
  ];

  return (
    <section id="property" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">Das Objekt</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ferienwohnung A57 – Ihr Stück Freiheit
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            Eingebettet in eine gepflegte Ferienanlage nahe dem Nationalpark Wittow – traumhafte Lage,
            idyllische Natur und die Ostsee direkt vor der Tür.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image grid */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-3"
          >
            <img
              src="/images/aerial_a57.jpg"
              alt="Luftaufnahme Ferienwohnung A57"
              className="col-span-2 rounded-2xl object-cover h-64 w-full shadow-lg"
            />
            <img
              src="/images/beach_cliffs.jpg"
              alt="Strandblick Rügen"
              className="rounded-xl object-cover h-44 w-full shadow-md"
            />
            <img
              src="/images/beach_sand.jpg"
              alt="Sandstrand Rügen"
              className="rounded-xl object-cover h-44 w-full shadow-md"
            />
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-card border border-border rounded-2xl p-8 shadow-md mb-6">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Kaufpreis</div>
                  <div className="text-3xl font-bold text-primary">140.000 €</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Lage</div>
                  <div className="font-semibold text-foreground">Bakenberg, Rügen</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Objekt-Nr.</div>
                  <div className="font-semibold text-foreground">A57</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Status</div>
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold">
                    <CheckCircle className="w-3 h-3" /> Verfügbar
                  </span>
                </div>
              </div>
              <button
                onClick={() => scrollToSection('contact')}
                className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-semibold text-base hover:opacity-90 transition-all hover:scale-[1.02] shadow-md"
              >
                Jetzt Besichtigung anfragen
              </button>
            </div>

            <ul className="space-y-2.5">
              {features.map((f, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 text-foreground/80"
                >
                  <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm">{f}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}


// ─── Rental Section ────────────────────────────────────────────────────────────
function RentalSection() {
  const [view, setView] = useState<'bar' | 'line'>('bar');
  const annualIncome = RENTAL_DATA.reduce((s, m) => s + m.potentialMonthlyIncome, 0);

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; dataKey: string }>; label?: string }) => {
    if (active && payload && payload.length) {
      const month = RENTAL_DATA.find(m => m.shortMonth === label);
      return (
        <div className="bg-white border border-border rounded-xl p-3 shadow-lg text-sm">
          <div className="font-semibold text-foreground mb-1">{month?.month}</div>
          <div className="text-primary">💰 {formatCurrency(payload[0].value)} Ertrag</div>
          <div className="text-muted-foreground">🌙 {month?.nightlyRate} €/Nacht</div>
          <div className="text-muted-foreground">📅 Auslastung: {month?.occupancyPct} %</div>
          <div className="mt-1">
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
              style={{ backgroundColor: SEASON_COLORS[month?.season ?? 'nebensaison'] }}
            >
              {SEASON_LABELS[month?.season ?? 'nebensaison']}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <section id="rental" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">Rendite & Vermietung</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ganzjährig vermieten auf Rügen
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            Rügen ist das ganze Jahr attraktiv. Die folgende Übersicht zeigt die aktuellen Marktpreise 2026
            und das Einkommenspotenzial Ihrer Ferienwohnung nach Monat.
          </p>
        </motion.div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Jahresumsatz (Potenzial)', value: formatCurrency(annualIncome), sub: 'bei Marktpreisen Rügen 2026', color: 'text-primary' },
            { label: 'Hochsaison Juli/Aug', value: '195–205 €', sub: 'pro Nacht · Auslastung bis 95 %', color: 'text-accent' },
            { label: 'Vorsaison Apr–Jun', value: '120–155 €', sub: 'pro Nacht · 50–75 % Auslastung', color: 'text-chart-4' },
            { label: 'Nebensaison', value: '85–95 €', sub: 'pro Nacht · auch im Winter buchbar', color: 'text-muted-foreground' },
          ].map((kpi, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-card border border-border rounded-2xl p-5 shadow-sm"
            >
              <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">{kpi.label}</div>
              <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{kpi.sub}</div>
            </motion.div>
          ))}
        </div>

        {/* Chart toggle */}
        <div className="flex gap-2 mb-6 justify-end">
          <button
            onClick={() => setView('bar')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${view === 'bar' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
          >
            Balken
          </button>
          <button
            onClick={() => setView('line')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${view === 'line' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
          >
            Kurve
          </button>
        </div>

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-card border border-border rounded-2xl p-6 shadow-sm mb-8"
        >
          <h3 className="font-semibold text-foreground mb-4">Monatliches Mieteinnahmen-Potenzial (€) – Rügen Marktpreise 2026</h3>
          <ResponsiveContainer width="100%" height={320}>
            {view === 'bar' ? (
              <BarChart data={RENTAL_DATA} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="shortMonth" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${v} €`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="potentialMonthlyIncome" radius={[6, 6, 0, 0]}>
                  {RENTAL_DATA.map((entry, index) => (
                    <Cell key={index} fill={SEASON_COLORS[entry.season]} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={RENTAL_DATA} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="shortMonth" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${v} €`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="potentialMonthlyIncome"
                  name="Mieteinnahmen"
                  stroke="var(--primary)"
                  strokeWidth={2.5}
                  dot={{ fill: 'var(--primary)', r: 4 }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </motion.div>

        {/* Season Legend */}
        <div className="flex flex-wrap gap-4 justify-center">
          {Object.entries(SEASON_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2 text-sm text-foreground/70">
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: SEASON_COLORS[key] }} />
              {label}
            </div>
          ))}
        </div>

        {/* Price table */}
        <div className="mt-10 bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-border bg-muted/50">
            <h3 className="font-semibold text-foreground">Übersicht: Mietpreise pro Nacht & Woche nach Saison (Rügen 2026)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Saison</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Zeitraum</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Nacht</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Woche</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Auslastung</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { season: 'nebensaison',  period: 'Jan–Mär / Nov–Dez', night: '80 – 95 €',   week: '560 – 665 €',   occ: '15 – 25 %' },
                  { season: 'vorsaison',    period: 'Apr–Jun / Sep–Okt', night: '110 – 160 €',  week: '770 – 1.120 €', occ: '50 – 75 %' },
                  { season: 'hauptsaison',  period: 'Juli – August',     night: '180 – 210 €',  week: '1.260 – 1.470 €', occ: '85 – 95 %' },
                  { season: 'hochsaison',   period: 'Weihnachten/Silvester', night: '180 – 200 €', week: '1.260 – 1.400 €', occ: 'bis 65 %' },
                ].map((row, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                        style={{ backgroundColor: SEASON_COLORS[row.season] }}
                      >
                        {SEASON_LABELS[row.season]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground/70">{row.period}</td>
                    <td className="px-4 py-3 text-right font-medium text-foreground">{row.night}</td>
                    <td className="px-4 py-3 text-right font-medium text-foreground">{row.week}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{row.occ}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 bg-muted/20 text-xs text-muted-foreground">
            * Preisangaben basieren auf aktuellen Marktpreisen auf Rügen (2026). Quellen: Tourismusverband MV, regionale Vermieterstatistiken.
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Financing Section ─────────────────────────────────────────────────────────
function FinancingSection() {
  const PURCHASE_PRICE = 140000;
  const [downPayment, setDownPayment] = useState(28000);
  const [interestRate, setInterestRate] = useState(3.5);
  const [termYears, setTermYears] = useState(20);

  const result = calculateFinancing(PURCHASE_PRICE, downPayment, interestRate, termYears);
  const downPct = Math.round((downPayment / PURCHASE_PRICE) * 100);

  return (
    <section id="financing" className="py-20 bg-muted">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">Finanzierungsrechner</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Plötzlich machbar!
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            Berechnen Sie Ihre monatliche Rate und sehen Sie, wie erschwinglich Ihr Traum
            am Meer wirklich ist – ganz ohne Banktermin.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Sliders */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-8"
          >
            <div>
              <div className="flex justify-between mb-2">
                <label className="font-medium text-sm text-foreground flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-primary" /> Kaufpreis
                </label>
                <span className="font-bold text-primary">140.000 €</span>
              </div>
              <div className="h-2 bg-muted rounded-full">
                <div className="h-2 bg-primary rounded-full w-full" />
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="font-medium text-sm text-foreground">Eigenkapital</label>
                <span className="font-bold text-primary">{downPayment.toLocaleString('de-DE')} € ({downPct} %)</span>
              </div>
              <Slider
                min={0}
                max={140000}
                step={1000}
                value={[downPayment]}
                onValueChange={([v]) => setDownPayment(v)}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0 €</span><span>140.000 €</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="font-medium text-sm text-foreground">Zinssatz (p.a.)</label>
                <span className="font-bold text-primary">{interestRate.toFixed(1)} %</span>
              </div>
              <Slider
                min={0.5}
                max={8}
                step={0.1}
                value={[interestRate]}
                onValueChange={([v]) => setInterestRate(v)}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0,5 %</span><span>8 %</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="font-medium text-sm text-foreground">Laufzeit</label>
                <span className="font-bold text-primary">{termYears} Jahre</span>
              </div>
              <Slider
                min={5}
                max={30}
                step={1}
                value={[termYears]}
                onValueChange={([v]) => setTermYears(v)}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>5 J.</span><span>30 J.</span>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col gap-4"
          >
            <div className="bg-primary text-primary-foreground rounded-2xl p-8 shadow-lg flex-1">
              <div className="text-primary-foreground/70 text-sm mb-2">Monatliche Rate</div>
              <div className="text-5xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                {result.monthlyRate.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
              </div>
              <div className="text-primary-foreground/70 text-sm">pro Monat</div>
              <div className="mt-6 border-t border-primary-foreground/20 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-primary-foreground/70">Darlehensbetrag</span>
                  <span className="font-medium">{(PURCHASE_PRICE - downPayment).toLocaleString('de-DE')} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-primary-foreground/70">Gesamtzinsen</span>
                  <span className="font-medium">{result.totalInterest.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €</span>
                </div>
                <div className="flex justify-between text-sm font-semibold border-t border-primary-foreground/20 pt-2">
                  <span>Gesamtkosten</span>
                  <span>{result.totalCost.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €</span>
                </div>
              </div>
            </div>

            <div className="bg-accent/15 border border-accent/30 rounded-2xl p-5">
              <div className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-accent" /> Mieteinnahmen vs. Rate
              </div>
              <p className="text-sm text-foreground/70">
                Bei durchschnittlicher Belegung erwirtschaftet Ihre Wohnung{' '}
                <strong className="text-foreground">ca. 1.400 €/Monat</strong> an Mieteinnahmen – das kann
                Ihre Finanzierungsrate erheblich decken!
              </p>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="w-full bg-accent text-accent-foreground py-3 rounded-xl font-semibold hover:opacity-90 transition-all hover:scale-[1.02] shadow"
            >
              Finanzierungsberatung anfragen →
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Excursions Section ────────────────────────────────────────────────────────
function ExcursionsSection() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  return (
    <section id="excursions" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">Ausflugsziele</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Rügen entdecken – ab Bakenberg
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            Von Ihrer Ferienwohnung aus erreichen Sie alle Highlights der Insel schnell und bequem.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Radial Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-6 shadow-md"
          >
            <h3 className="font-semibold text-foreground mb-4 text-center">Entfernungskarte ab Bakenberg</h3>
            <div className="relative mx-auto" style={{ width: 320, height: 320 }}>
              {/* Distance rings */}
              {[60, 120, 180, 240].map(r => (
                <div
                  key={r}
                  className="absolute rounded-full border border-border/40"
                  style={{ width: r * 2, height: r * 2, top: 160 - r, left: 160 - r }}
                />
              ))}
              {/* Distance labels */}
              {[
                { r: 60, label: '15 km' },
                { r: 120, label: '30 km' },
                { r: 180, label: '45 km' },
                { r: 240, label: '60 km' },
              ].map(({ r, label }) => (
                <span
                  key={label}
                  className="absolute text-[10px] text-muted-foreground"
                  style={{ top: 160 - r - 8, left: 162 }}
                >
                  {label}
                </span>
              ))}
              {/* Center (Bakenberg) */}
              <div
                className="absolute z-10 flex flex-col items-center"
                style={{ top: 160 - 20, left: 160 - 20 }}
              >
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-lg">
                  <HomeIcon className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-xs font-semibold text-primary mt-1 whitespace-nowrap">A57</span>
              </div>
              {/* Destination dots */}
              {EXCURSIONS.map((exc, i) => {
                const rad = (exc.angle - 90) * (Math.PI / 180);
                const scale = exc.distancePx / 57; // normalize to max ~57km = 240px
                const px = Math.cos(rad) * scale * 240;
                const py = Math.sin(rad) * scale * 240;
                const x = 160 + px - 14;
                const y = 160 + py - 14;
                return (
                  <motion.button
                    key={i}
                    className="absolute z-10 flex flex-col items-center cursor-pointer"
                    style={{ top: y, left: x }}
                    whileHover={{ scale: 1.2 }}
                    onClick={() => setActiveIdx(i === activeIdx ? null : i)}
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center shadow-md text-sm"
                      style={{
                        backgroundColor: activeIdx === i ? 'var(--accent)' : 'white',
                        border: '2px solid var(--primary)',
                      }}
                    >
                      {exc.icon}
                    </div>
                    <span className="text-[9px] font-semibold text-foreground mt-0.5 whitespace-nowrap max-w-16 text-center leading-tight">
                      {exc.name.split(' ')[0]}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            {activeIdx !== null && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 bg-primary/10 border border-primary/20 rounded-xl p-4"
              >
                <div className="font-semibold text-foreground text-sm">{EXCURSIONS[activeIdx].name}</div>
                <div className="text-xs text-primary font-medium">{EXCURSIONS[activeIdx].distance} ab Bakenberg</div>
                <div className="text-xs text-muted-foreground mt-1">{EXCURSIONS[activeIdx].description}</div>
              </motion.div>
            )}
          </motion.div>

          {/* List */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-3"
          >
            {EXCURSIONS.map((exc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                  activeIdx === i
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                }`}
                onClick={() => setActiveIdx(i === activeIdx ? null : i)}
              >
                <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center text-xl shrink-0">
                  {exc.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-foreground text-sm">{exc.name}</span>
                    <span className="text-xs font-bold text-primary shrink-0">{exc.distance}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{exc.description}</p>
                </div>
              </motion.div>
            ))}
            <p className="text-xs text-muted-foreground pt-2">
              * Entfernungen per Auto ab Bakenberg/Dranske, Insel Rügen.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Rügen Section ─────────────────────────────────────────────────────────────
function RuegenSection() {
  const facts = [
    { icon: <Trees className="w-6 h-6" />, title: 'UNESCO Weltnaturerbe', desc: 'Buchenwälder Rügens gehören zum Weltnaturerbe der UNESCO' },
    { icon: <Sun className="w-6 h-6" />, title: 'Sonnenverwöhnte Insel', desc: 'Überdurchschnittlich viele Sonnenstunden im bundesweiten Vergleich' },
    { icon: <MapPin className="w-6 h-6" />, title: 'Größte deutsche Insel', desc: '926 km² – Naturvielfalt von Kreideküste bis Sandstrand' },
    { icon: <Anchor className="w-6 h-6" />, title: '60 km Sandstrand', desc: 'Traumhafte Strandabschnitte für jeden Geschmack' },
    { icon: <Star className="w-6 h-6" />, title: 'Bäderarchitektur', desc: 'Einzigartige historische Architektur in Binz, Sellin & Co.' },
    { icon: <TrendingUp className="w-6 h-6" />, title: 'Top Tourismusdestination', desc: '33,3 Mio. Übernachtungen in MV jährlich – stabiler Markt' },
  ];
  return (
    <section id="ruegen" className="py-20 bg-muted relative overflow-hidden">
      {/* bg image */}
      <div className="absolute inset-0">
        <img src="/images/beach_sand.jpg" alt="Strand Rügen" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-muted/90 to-muted/95" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">Die Insel</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Warum Rügen so besonders ist
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            Rügen ist nicht nur Deutschlands größte Insel – sie ist ein einzigartiges Naturparadies,
            das Besucher Jahr für Jahr begeistert und als Investitionsstandort überzeugt.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {facts.map((fact, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-card/80 border border-border rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                {fact.icon}
              </div>
              <h3 className="font-semibold text-foreground mb-1">{fact.title}</h3>
              <p className="text-sm text-muted-foreground">{fact.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 bg-primary/10 border border-primary/20 rounded-2xl p-6 text-center max-w-3xl mx-auto"
        >
          <p className="text-foreground/80 text-base leading-relaxed">
            <strong className="text-primary">Kap Arkona</strong> – Rügens nördlichste Spitze mit historischen Leuchttürmen –
            ist von Bakenberg in nur <strong>15 km</strong> erreichbar. Ein Ausflug, den kein Gast vergisst.
            Dazu Störtebeker Festspiele, der Rasende Roland und die Kreideküste: Rügen ist immer eine Reise wert.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Contact Section ───────────────────────────────────────────────────────────
function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState<Partial<typeof form>>({});

  const validate = () => {
    const e: Partial<typeof form> = {};
    if (!form.name.trim()) e.name = 'Bitte Namen eingeben';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Gültige E-Mail erforderlich';
    if (!form.message.trim()) e.message = 'Nachricht erforderlich';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const subject = encodeURIComponent(`Anfrage Ferienwohnung A57 – Rügen von ${form.name}`);
    const body = encodeURIComponent(
      `Guten Tag,\n\nfolgende Anfrage zur Ferienwohnung A57 auf Rügen ist eingegangen:\n\n` +
      `Name:     ${form.name}\n` +
      `E-Mail:   ${form.email}\n` +
      `Telefon:  ${form.phone || '–'}\n\n` +
      `Nachricht:\n${form.message}\n\n` +
      `---\nGesendet über die Verkaufsseite A57 · Bakenberg · Rügen`
    );
    window.location.href = `mailto:info@pixnetmedia.de?subject=${subject}&body=${body}`;

    setSubmitted(true);
  };

  return (
    <section id="contact" className="py-20 bg-background">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">Kontakt</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ihr Traum beginnt hier
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            Stellen Sie Ihre Fragen, buchen Sie eine Besichtigung oder fordern Sie
            weitere Unterlagen zur Ferienwohnung A57 an.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-10">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:col-span-2 space-y-6"
          >
            <div>
              <img
                src="/images/aerial_a57.jpg"
                alt="A57 Lage"
                className="rounded-2xl w-full h-40 object-cover shadow-md mb-6"
              />
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm text-foreground">Lage</div>
                    <div className="text-sm text-muted-foreground">Bakenberg, Wittow<br />Insel Rügen, Deutschland</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Euro className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm text-foreground">Kaufpreis</div>
                    <div className="text-sm text-muted-foreground">140.000 € · inkl. Inventar</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm text-foreground">Online-Formular</div>
                    <a
                      href="http://formular.pixnetmedia.de/A57/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      formular.pixnetmedia.de/A57/
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm text-foreground">Telefon</div>
                    <div className="text-sm text-muted-foreground">Auf Anfrage</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:col-span-3"
          >
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center"
              >
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">Vielen Dank für Ihre Anfrage!</h3>
                <p className="text-muted-foreground">
                  Wir melden uns schnellstmöglich bei Ihnen. Wir freuen uns auf Ihre Besichtigung der Ferienwohnung A57.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Name *</label>
                    <Input
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Ihr vollständiger Name"
                      className={errors.name ? 'border-destructive' : ''}
                    />
                    {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Telefon</label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="+49 ..."
                      type="tel"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">E-Mail *</label>
                  <Input
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="ihre@email.de"
                    type="email"
                    className={errors.email ? 'border-destructive' : ''}
                  />
                  {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Nachricht *</label>
                  <Textarea
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder="Ich interessiere mich für die Ferienwohnung A57 und würde gerne eine Besichtigung vereinbaren ..."
                    rows={5}
                    className={errors.message ? 'border-destructive' : ''}
                  />
                  {errors.message && <p className="text-destructive text-xs mt-1">{errors.message}</p>}
                </div>
                <div className="flex items-start gap-2">
                  <input type="checkbox" id="privacy" required className="mt-1 accent-primary" />
                  <label htmlFor="privacy" className="text-xs text-muted-foreground leading-relaxed">
                    Ich stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *
                  </label>
                </div>
                <Button type="submit" className="w-full py-3 text-base font-semibold gap-2">
                  <Send className="w-4 h-4" /> Anfrage absenden
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Oder direkt über das{' '}
                  <a
                    href="http://formular.pixnetmedia.de/A57/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2"
                  >
                    Online-Formular
                  </a>{' '}
                  anfragen.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="bg-foreground text-background py-10">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 font-semibold text-lg">
          <Anchor className="w-5 h-5 text-accent" />
          <span style={{ fontFamily: "'Playfair Display', serif" }}>A57 · Rügen</span>
        </div>
        <div className="text-background/60 text-sm text-center">
          Ferienwohnung A57 · Bakenberg · Insel Rügen · Kaufpreis 140.000 €
        </div>
        <div className="flex gap-4 text-sm text-background/60">
          <button onClick={() => scrollToSection('contact')} className="hover:text-accent transition-colors">Kontakt</button>
          <span>|</span>
          <a
            href="http://formular.pixnetmedia.de/A57/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-accent transition-colors"
          >
            Formular
          </a>
        </div>
      </div>
    </footer>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <Hero />
        <HighlightsBar />
        <PropertySection />
        <RentalSection />
        <FinancingSection />
        <ExcursionsSection />
        <RuegenSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
